import React, { memo, useMemo, useCallback, useRef } from 'react';

// Système de mémoization intelligente pour les composants
export const SmartMemo = (Component, customCompare = null) => {
  return memo(Component, customCompare || ((prevProps, nextProps) => {
    // Comparaison intelligente par défaut
    const prevKeys = Object.keys(prevProps);
    const nextKeys = Object.keys(nextProps);
    
    if (prevKeys.length !== nextKeys.length) return false;
    
    for (const key of prevKeys) {
      if (typeof prevProps[key] === 'function' && typeof nextProps[key] === 'function') {
        // Skip function comparison pour éviter re-renders inutiles
        continue;
      }
      
      if (Array.isArray(prevProps[key]) && Array.isArray(nextProps[key])) {
        if (prevProps[key].length !== nextProps[key].length) return false;
        if (JSON.stringify(prevProps[key]) !== JSON.stringify(nextProps[key])) return false;
      } else if (typeof prevProps[key] === 'object' && typeof nextProps[key] === 'object') {
        if (JSON.stringify(prevProps[key]) !== JSON.stringify(nextProps[key])) return false;
      } else if (prevProps[key] !== nextProps[key]) {
        return false;
      }
    }
    
    return true;
  }));
};

// Hook pour memoization automatique des callbacks
export const useStableCallback = (callback, deps) => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  
  return useCallback((...args) => callbackRef.current(...args), deps);
};

// Hook pour memoization des calculs coûteux  
export const useExpensiveComputation = (computeFn, deps, options = {}) => {
  const { 
    threshold = 50, // ms
    cache = true,
    debug = false 
  } = options;
  
  const cacheRef = useRef(new Map());
  const performanceRef = useRef({ calls: 0, totalTime: 0 });
  
  return useMemo(() => {
    const start = performance.now();
    const depsKey = JSON.stringify(deps);
    
    // Vérifier le cache si activé
    if (cache && cacheRef.current.has(depsKey)) {
      const cached = cacheRef.current.get(depsKey);
      if (debug) console.log('ComponentOptimizer: Cache hit for', depsKey);
      return cached;
    }
    
    // Exécuter le calcul
    const result = computeFn();
    const duration = performance.now() - start;
    
    // Mise à jour des métriques
    performanceRef.current.calls++;
    performanceRef.current.totalTime += duration;
    
    // Warning si le calcul est coûteux
    if (duration > threshold && debug) {
      console.warn(`ComponentOptimizer: Expensive computation detected (${duration.toFixed(2)}ms)`, {
        deps: depsKey,
        averageTime: performanceRef.current.totalTime / performanceRef.current.calls
      });
    }
    
    // Mise en cache
    if (cache) {
      cacheRef.current.set(depsKey, result);
      
      // Limiter la taille du cache
      if (cacheRef.current.size > 100) {
        const firstKey = cacheRef.current.keys().next().value;
        cacheRef.current.delete(firstKey);
      }
    }
    
    return result;
  }, deps);
};

// HOC pour optimisation automatique des listes
export const withListOptimization = (ListComponent) => {
  return SmartMemo(({ items = [], renderItem, ...props }) => {
    // Memoization des items rendus
    const memoizedItems = useMemo(() => {
      return items.map((item, index) => ({
        ...item,
        _optimizedKey: `${item.id || index}-${JSON.stringify(item).slice(0, 50)}`
      }));
    }, [items]);
    
    // Callback stable pour le rendu
    const stableRenderItem = useStableCallback(renderItem, []);
    
    return (
      <ListComponent
        {...props}
        items={memoizedItems}
        renderItem={stableRenderItem}
      />
    );
  });
};

// Hook pour détection de re-renders inutiles  
export const useRenderTracker = (componentName, props = {}) => {
  const renderCount = useRef(0);
  const prevProps = useRef(props);
  
  renderCount.current++;
  
  if (process.env.NODE_ENV === 'development') {
    const changedProps = {};
    
    Object.keys(props).forEach(key => {
      if (prevProps.current[key] !== props[key]) {
        changedProps[key] = {
          old: prevProps.current[key],
          new: props[key]
        };
      }
    });
    
    if (Object.keys(changedProps).length > 0) {
      console.log(`RenderTracker [${componentName}] - Render #${renderCount.current}`, {
        changedProps,
        totalRenders: renderCount.current
      });
    }
  }
  
  prevProps.current = props;
  
  return renderCount.current;
};

// Système de virtualisation intelligente
export const VirtualizedContainer = SmartMemo(({
  items,
  itemHeight = 50,
  containerHeight = 400,
  renderItem,
  overscan = 5,
  className = ''
}) => {
  const [scrollTop, setScrollTop] = React.useState(0);
  
  const totalHeight = items.length * itemHeight;
  const startIndex = Math.max(0, Math.floor(scrollTop / itemHeight) - overscan);
  const endIndex = Math.min(
    items.length - 1,
    Math.floor((scrollTop + containerHeight) / itemHeight) + overscan
  );
  
  const visibleItems = useMemo(() => {
    return items.slice(startIndex, endIndex + 1).map((item, index) => ({
      item,
      index: startIndex + index,
      top: (startIndex + index) * itemHeight
    }));
  }, [items, startIndex, endIndex, itemHeight]);
  
  const handleScroll = useStableCallback((e) => {
    setScrollTop(e.target.scrollTop);
  }, []);
  
  return (
    <div
      className={`virtualized-container ${className}`}
      style={{ height: containerHeight, overflow: 'auto' }}
      onScroll={handleScroll}
    >
      <div style={{ height: totalHeight, position: 'relative' }}>
        {visibleItems.map(({ item, index, top }) => (
          <div
            key={item.id || index}
            style={{
              position: 'absolute',
              top: top,
              left: 0,
              right: 0,
              height: itemHeight
            }}
          >
            {renderItem(item, index)}
          </div>
        ))}
      </div>
    </div>
  );
});

// Optimiseur global de performance
export class PerformanceOptimizer {
  static getInstance() {
    if (!this.instance) {
      this.instance = new PerformanceOptimizer();
    }
    return this.instance;
  }
  
  constructor() {
    this.metrics = {
      componentRenders: new Map(),
      expensiveComputations: new Map(),
      recommendations: []
    };
  }
  
  trackComponentRender(componentName) {
    const current = this.metrics.componentRenders.get(componentName) || 0;
    this.metrics.componentRenders.set(componentName, current + 1);
    
    // Recommandation si trop de re-renders
    if (current > 20) {
      this.addRecommendation(
        `Le composant ${componentName} a été rendu ${current} fois. Considérez l'optimisation avec React.memo ou useMemo.`,
        'warning'
      );
    }
  }
  
  addRecommendation(message, level = 'info') {
    this.metrics.recommendations.push({
      message,
      level,
      timestamp: Date.now()
    });
    
    // Garder seulement les 50 dernières
    if (this.metrics.recommendations.length > 50) {
      this.metrics.recommendations = this.metrics.recommendations.slice(-50);
    }
  }
  
  getMetrics() {
    return {
      ...this.metrics,
      summary: {
        totalComponentRenders: Array.from(this.metrics.componentRenders.values()).reduce((a, b) => a + b, 0),
        uniqueComponents: this.metrics.componentRenders.size,
        activeRecommendations: this.metrics.recommendations.length
      }
    };
  }
  
  exportReport() {
    const metrics = this.getMetrics();
    
    return {
      timestamp: new Date().toISOString(),
      performance: {
        componentRenders: Object.fromEntries(metrics.componentRenders),
        expensiveComputations: Object.fromEntries(metrics.expensiveComputations),
        summary: metrics.summary
      },
      recommendations: metrics.recommendations,
      optimizationTips: [
        'Utilisez React.memo pour les composants qui reçoivent souvent les mêmes props',
        'Employez useMemo pour les calculs coûteux',
        'Privilégiez useCallback pour les fonctions passées en props',
        'Implémentez la virtualisation pour les listes > 100 éléments',
        'Optimisez les images avec lazy loading et compression'
      ]
    };
  }
} 